import React, { useState, useEffect } from 'react';
import { CheckCircle, Award, Users, Mail } from 'lucide-react';
import { heroImages } from '../mockData';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 7000);
    return () => clearInterval(interval);
  }, []);

  const scrollToLeistungen = () => {
    document.getElementById('leistungen').scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToKontakt = () => {
    document.getElementById('kontakt').scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
    { icon: CheckCircle, title: 'Modern & Sicher' },
    { icon: Award, title: 'Staatlich geprüft' },
    { icon: Users, title: 'Regional' }
  ];

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden pt-24 md:pt-32 lg:pt-40">
      {/* Slider Background */}
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${image})`,
                transform: index === currentSlide ? 'scale(1.15)' : 'scale(1)',
                transition: 'transform 15s ease-out'
              }}
            />
          </div>
        ))}
      </div>

      {/* Gradient Overlay - angepasst für Mobile */}
      <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-l from-black/80 via-black/60 to-black/40 md:from-[#DC2626]/90 md:via-[#DC2626]/60 md:to-transparent"></div>
      <div className="absolute inset-0 bg-black/30 md:bg-black/40"></div>

      {/* Content */}
      <div className="relative z-10 w-full">
        <div className="container mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-0">
          <div className="max-w-xl text-white space-y-4 sm:space-y-6 md:space-y-8">
            
            {/* Roter Strich - kleiner auf Mobile */}
            <div className="w-12 sm:w-16 h-1 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>

            {/* Main Slogan - responsive Größe */}
            <div>
              <h1 className="font-bold leading-tight" style={{ textShadow: '0 4px 12px rgba(0,0,0,0.7)' }}>
                <span className="block text-white text-3xl sm:text-5xl md:text-6xl lg:text-7xl mb-1 sm:mb-3">
                  Aus alt wird
                </span>
                <span 
                  className="block text-[#DC2626] text-3xl sm:text-5xl md:text-6xl lg:text-7xl" 
                  style={{ textShadow: '0 0 20px rgba(220,38,38,0.5), 0 4px 12px rgba(0,0,0,0.7)' }}
                >
                  außergewöhnlich
                </span>
              </h1>
            </div>

            {/* Subheading - GRÖSSER */}
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed max-w-lg font-medium" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
              Ihr vertrauensvoller Partner für erstklassige Sanierung historischer Gebäude in Stuttgart und Umgebung
            </p>

            {/* CTA Buttons - volle Breite auf Mobile */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
              <button
                onClick={scrollToKontakt}
                className="inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base shadow-2xl hover:bg-[#B91C1C] transition-all duration-300 w-full sm:w-auto"
                data-testid="hero-cta-anfragen"
              >
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                Jetzt anfragen
              </button>
              <button
                onClick={scrollToLeistungen}
                className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm border border-white/30 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base shadow-2xl hover:bg-white/30 transition-all duration-300 w-full sm:w-auto"
                data-testid="hero-cta-leistungen"
              >
                Leistungen entdecken
              </button>
            </div>

            {/* Features - Responsive Grid */}
            <div className="grid grid-cols-3 gap-2 sm:flex sm:gap-4 pt-2 sm:pt-4">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex flex-col sm:flex-row items-center gap-1 sm:gap-3 px-2 sm:px-4 md:px-6 py-3 sm:py-4 rounded-lg bg-white/15 sm:bg-white/20 backdrop-blur-xl border border-white/30 sm:border-white/40 shadow-xl"
                >
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7 text-white flex-shrink-0" />
                  <span className="font-semibold sm:font-bold text-xs sm:text-sm md:text-base text-white text-center sm:text-left sm:whitespace-nowrap">
                    {feature.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 sm:bottom-6 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
        {heroImages.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentSlide ? 'w-6 sm:w-8 h-2 bg-white' : 'w-2 h-2 bg-white/50'
            }`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
